import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  getEducationSettingsQuery,
  updateEducationSettingsMutation
} from "../../../Graphql/querries/education/settingsQuery";
import {
  EducationSettings,
  GetEducationSettingsResponse,
  UpdateEducationSettingsInput,
  UpdateEducationSettingsResponse
} from "../../../components/Education/Settings/types/educationSettings";
import { GraphQLData, unwrapGraphQLOperation } from "./educationGraphQLUtils";

export const useEducationSettings = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["educationSettings"],
    queryFn: async (): Promise<EducationSettings | null> => {
      const data = await fetchGraphQL<
        GraphQLData<GetEducationSettingsResponse, "getEducationSettings">
      >(getEducationSettingsQuery, {}, setAlert);
      const response = unwrapGraphQLOperation(data, "getEducationSettings");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return null;
      }
      return response.settings ?? null;
    }
  });
};

export const useUpdateEducationSettings = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateEducationSettings"],
    mutationFn: async (input: UpdateEducationSettingsInput) => {
      const data = await fetchGraphQL<
        GraphQLData<UpdateEducationSettingsResponse, "updateEducationSettings">
      >(updateEducationSettingsMutation, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "updateEducationSettings");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
      return response.settings ?? null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["educationSettings"] });
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        message: "Education settings saved."
      });
    }
  });
};
